import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useEnterpriseAccountResetPassword } from 'domains/public/api/auth/password-reset/enterpriseAccountResetPassword.hooks'
import {
  isTokenExpiredError,
  isNotFoundError,
  isSecurityError,
} from 'api/utils/apollo/errors/isError.hooks'
import { type DashMessages } from 'intl/intl.types'
import { useFormErrorMessageMap } from 'utils/react-hook-form/reactHookForm.hooks'

interface Props {
  token: string
}

type PasswordResetFormData = {
  password: string
  confirmPassword: string
}

const defaultValues: PasswordResetFormData = {
  password: '',
  confirmPassword: '',
}

export const usePasswordResetDetails = ({ token }: Props) => {
  const { resetPassword, apiLoading, apiSuccess, apiError } = useEnterpriseAccountResetPassword()

  const { register, handleSubmit, errors, watch, setValue } = useForm<PasswordResetFormData>({
    defaultValues,
  })

  const onSubmit = handleSubmit(({ password }) => {
    resetPassword({
      rawToken: token,
      password,
    })
  })

  const passwordFieldRef = register({ required: true, minLength: 8 })

  const confirmPasswordFieldRef = register({
    required: true,
    validate: confirmPasswordValue => watch('password') === confirmPasswordValue,
  })

  const passwordErrorMessageId = useFormErrorMessageMap({
    required: 'public.passwordReset.form.fields.password.error.required',
    minLength: 'public.passwordReset.form.fields.password.error.minLength',
  })(errors.password)

  const confirmPasswordErrorMessageId = useFormErrorMessageMap({
    validate: 'public.passwordReset.form.fields.confirmPassword.error.validate',
  })(errors.confirmPassword)

  const tokenErrorMessageId: DashMessages | undefined = useMemo(() => {
    if (!apiError) return undefined

    if (isTokenExpiredError(apiError)) {
      return 'public.passwordReset.content.error.expiredToken'
    }

    if (isNotFoundError(apiError)) {
      return 'public.passwordReset.content.error.notFound'
    }

    return 'public.passwordReset.content.error.common'
  }, [apiError])

  const formErrorMessageId: DashMessages | undefined = useMemo(() => {
    if (!apiError) return undefined

    if (isSecurityError(apiError)) {
      return 'public.passwordReset.form.error.insecurePassword'
    }

    // Default to no formErrorMessageId
    // tokenErrorMessageId should be set instead
    return undefined
  }, [apiError])

  return {
    onSubmit,
    passwordFieldRef,
    passwordErrorMessageId,
    confirmPasswordFieldRef,
    confirmPasswordErrorMessageId,
    disableFormFields: apiLoading || apiSuccess,
    passwordResetSuccess: apiSuccess,
    tokenErrorMessageId,
    formErrorMessageId,
    _setValue: setValue,
  }
}
